import { IconX } from "@decibeltrade/react-icons";
import { useTranslations } from "next-intl";
import { css, cva, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { Clickable } from "./clickable";

export type BannerVariant = "warning" | "critical" | "positive";

export interface BannerProps {
  children: React.ReactNode;
  variant: BannerVariant;
  className?: string;
  ref?: React.RefObject<HTMLDivElement | null>;
  onClick?: () => void;
  onDismiss?: () => void;
}

export function Banner({ children, variant, className, ref, onClick, onDismiss }: BannerProps) {
  const t = useTranslations("general");
  return (
    <div
      ref={ref}
      onClick={onClick}
      style={onClick ? { cursor: "pointer" } : undefined}
      className={cx(bannerStyles({ variant }), className)}
    >
      <span className={flex({ flex: "1", gap: "12" })}>{children}</span>
      {onDismiss && (
        <Clickable
          onClick={onDismiss}
          className={dismissButtonStyles({ variant })}
          ariaLabel={t("close")}
        >
          <IconX className={css({ w: "12", h: "12" })} />
        </Clickable>
      )}
    </div>
  );
}

const dismissButtonStyles = cva({
  base: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    w: "20",
    h: "20",
    border: "1px solid",
    bg: "transparent",
    cursor: "pointer",
    borderRadius: "full",
    transition: "all",
    transitionDuration: "200",
    transitionTimingFunction: "ease-in-out",
    _before: { rounded: "full!" },
  },
  variants: {
    variant: {
      warning: {
        borderColor: "banner.text",
        color: "banner.text",
        _hover: { bgColor: "banner.text", color: "banner.warning" },
      },
      critical: {
        borderColor: "banner.text",
        color: "banner.text",
        _hover: { bgColor: "banner.text", color: "banner.critical" },
      },
      positive: {
        borderColor: "banner.positiveText",
        color: "banner.positiveText",
        _hover: { bgColor: "banner.positiveText", color: "banner.positive" },
      },
    },
  },
});

export const bannerStyles = cva({
  base: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    px: "12",
    py: "8",
    textStyle: "highlight.md",
  },
  variants: {
    variant: {
      warning: {
        bg: "banner.warning",
        color: "banner.text",
      },
      critical: {
        bg: "banner.critical",
        color: "banner.text",
      },
      positive: {
        bg: "banner.positive",
        color: "banner.positiveText",
      },
    },
  },
});
